import React, { useEffect, useMemo, useState } from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { PhoneNumberInput, PhoneNumberInputValues } from "artisn-ui-react";
import { TextInput } from "artisn-ui-react";
import { useFetchCantones } from "@anefi/services";
import { useFetchCountries, useFetchProvinces } from "@anefi/services";
import { convertDataToDataSelect } from "@anefi/utils";
import { useCountries } from "@anefi/contexts";

import Styles from "./StepTwoLegalForm.styles";
import { StepTwoLegalFormProps as Props } from "./StepTwoLegalForm.types";
import { StepTwoLegalFormValues } from "./StepTwoLegalForm.types";
import useI18n from "hooks/useI18n";
import Select from "components/global/Select/Select";

const StepTwoLegalForm: React.FC<Props> = props => {
  const t = useI18n();
  const { register, formState, getValues, control, setValue } =
    useFormContext<StepTwoLegalFormValues>();
  const { data: countriesData } = useFetchCountries();
  const { countryISOCodes, selectedCountry } = useCountries();
  const { isoCode: defaultCountryISOCode } = selectedCountry;
  const watchCountry = useWatch({ control, name: "countryCodeWorkAddress" });
  const watchProvince = useWatch({ control, name: "homeAddressProvinceCode" });
  const watchCanton = useWatch({ control, name: "orgGeoCodeWorkAddress" });

  const { data: provincesData } = useFetchProvinces(watchCountry);
  const { data: cantonesData } = useFetchCantones(watchCountry, watchProvince);

  const [phone, setPhone] = useState<PhoneNumberInputValues>({
    countryCallingCode: "",
    countryCode: defaultCountryISOCode,
    international: "",
    internationalRaw: "",
    isPossible: true,
    isValid: true,
    national: getValues().additionalPhone ?? "",
    nationalRaw: ""
  });

  const countriesForSelect = useMemo(
    () => convertDataToDataSelect(countriesData, "code", "name"),
    [countriesData]
  );
  const provincesForSelect = convertDataToDataSelect(
    provincesData,
    "code",
    "name"
  );
  const cantonesForSelect = convertDataToDataSelect(
    cantonesData,
    "code",
    "name"
  );

  const errors = formState?.errors ?? {};
  const { countryCodeWorkAddress, homeAddressProvinceCode } = errors;
  const { numberWorkAddress } = errors;
  const { intersectionWorkAddress } = errors;
  const { additionalPhone: additionalPhoneError } = errors;
  const { mainStreetWorkAddress, orgGeoCodeWorkAddress } = errors;

  const countryName = useMemo(
    () =>
      countriesForSelect.find(item => item.key === watchCountry)?.value ?? "",
    [countriesForSelect, watchCountry]
  );
  const cantonName = useMemo(
    () => cantonesForSelect.find(item => item.key === watchCanton)?.value ?? "",
    [cantonesForSelect, watchCanton]
  );

  useEffect(() => {
    setValue("countryNameWorkAddress", countryName);
  }, [countryName, setValue]);

  useEffect(() => {
    setValue("orgGeoNameWorkAddress", cantonName);
  }, [cantonName, setValue]);

  return (
    <Styles className="StepTwoLegalForm">
      <p className="StepTwoLegalForm__title">
        Completa los datos para iniciar tu primera inversión
      </p>
      <Controller
        name="countryCodeWorkAddress"
        control={control}
        render={({ field }) => (
          <Select
            {...field}
            className="field"
            selectData={countriesForSelect}
            label={t.legalStepTwoForm.workCountry}
            placeholder={t.legalStepTwoForm.workCountry}
            errorMessage={countryCodeWorkAddress?.message}
          />
        )}
      />
      <Controller
        name="homeAddressProvinceCode"
        control={control}
        render={({ field }) => (
          <Select
            {...field}
            className="field"
            selectData={provincesForSelect}
            label={t.stepFourForm.province}
            placeholder={t.stepFourForm.province}
            errorMessage={homeAddressProvinceCode?.message}
          />
        )}
      />
      <Controller
        name="orgGeoCodeWorkAddress"
        control={control}
        render={({ field }) => (
          <Select
            {...field}
            className="field"
            selectData={cantonesForSelect}
            label={t.stepFourForm.canton}
            placeholder={t.stepFourForm.canton}
            errorMessage={orgGeoCodeWorkAddress?.message}
          />
        )}
      />
      <TextInput
        {...register("mainStreetWorkAddress")}
        className="field"
        label={t.legalStepTwoForm.principalStreet}
        placeholder={t.legalStepTwoForm.principalStreet}
        maxLength={50}
        errorMessage={mainStreetWorkAddress?.message}
      />
      <TextInput
        {...register("numberWorkAddress")}
        className="field"
        label={t.legalStepTwoForm.numberWorkAddress}
        placeholder={t.legalStepTwoForm.numberWorkAddress}
        maxLength={50}
        errorMessage={numberWorkAddress?.message}
      />
      <TextInput
        {...register("intersectionWorkAddress")}
        className="field"
        label={t.legalStepTwoForm.intersectionWorkAddress}
        placeholder={t.legalStepTwoForm.intersectionWorkAddress}
        maxLength={50}
        errorMessage={intersectionWorkAddress?.message}
      />
      <Controller
        name="additionalPhone"
        control={control}
        render={({ field }) => (
          <PhoneNumberInput
            {...field}
            className="field"
            label={"Teléfono"}
            countriesCodes={countryISOCodes}
            onChange={e => {
              setPhone(e);
              field.onChange(e.national);
            }}
            errorMessage={additionalPhoneError?.message}
            inputValue={phone.national}
            maxLength={12}
            placeholder="099 999 9999"
          />
        )}
      />
    </Styles>
  );
};

StepTwoLegalForm.defaultProps = {};

export default StepTwoLegalForm;
